import * as Constants from 'src/constants';
import { GENERAL_OPERATIONS } from 'src/constants';
import { useFeatureFlags } from 'src/context';
import { useCreateClientPermission } from 'src/features/crm/hooks/useCreateClientPermission';
import { useInProductNotificationsState } from 'src/hooks/useInProductNotificationsState';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import { RootState } from 'src/store';
import * as UserUtils from 'src/utils/UserUtils';

export const useRouteAccess = ({
  shouldPreviewAsClient = false,
}: {
  shouldPreviewAsClient?: boolean;
} = {}) => {
  const planStatus = usePlanStatus();
  const isUnAuth = useAppSelector((state: RootState) => state.user.isUnAuth);
  const isClient = useAppSelector(
    (state: RootState) => state.user.isClient || shouldPreviewAsClient === true,
  );

  const permissions = useAppSelector(
    (state: RootState) => state.user.data?.permissions,
  );

  const { canCreateClient } = useCreateClientPermission();
  const installs = useGetInstallsQuery();
  const { data: { moduleSettings } = {}, isLoading: isInstallsLoading } =
    installs;

  const {
    EnableAppBuilder,
    EnableStudioSidebar,
    EnableStudioConnectors,
  } = useFeatureFlags();
  const { isInboxInProductNotificationsDisabled } =
    useInProductNotificationsState(isClient);

  /* Since portalConfig object is not updated in real time, we need
   * to get the enable module state from the moduleSettings.
   * This method gets default-module active state from module settings.
   * @param moduleId: default module id
   * @returns
   */
  const getDefaultModuleStateFromModuleSettings = (moduleId: string) => {
    if (!moduleSettings || !moduleSettings.length) return true;
    const moduleItem = moduleSettings.find((mod) => moduleId === mod.id);
    if (!moduleItem) {
      return false;
    }

    if (moduleItem.disabled) {
      return false;
    }
    return true;
  };

  // Referrals are a paid perk, so the free plan joins Classic trials in not
  // seeing them. The trial clause stays because legacy Classic workspaces are
  // the only ones it can still match, and this rule must not change for them.
  // `studioPlan` is null on Classic/flag-off workspaces and reads as freemium
  // whenever the Studio plan is unresolved — in flight or failed request — so
  // this clause fails closed exactly like every other Studio entitlement gate.
  const hideReferrals =
    planStatus.isTrialing || planStatus.studio.studioPlan === 'freemium';

  const routeAccess = {
    allowProfilePage: !isUnAuth,
    allowCRMPages: UserUtils.hasPermission(Constants.PERM_MOD_CRM, permissions),
    // The Studio sidebar has no Dashboard, so the flag that swaps the sidebar
    // is what decides this — not `isStudioUser`. The two disagree for Classic
    // workspaces put on the studio-sidebar flag, which is every workspace once
    // the flag reaches 100%.
    allowHomePage: !isClient && !EnableStudioSidebar,
    allowMessagesPage:
      UserUtils.hasPermission(Constants.PERM_MOD_MESSAGES, permissions) &&
      getDefaultModuleStateFromModuleSettings('messages'),
    allowFilesPage:
      UserUtils.hasPermission(Constants.PERM_MOD_FILES, permissions) &&
      getDefaultModuleStateFromModuleSettings('files'),
    allowPaymentsPage:
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowClientPaymentsPage:
      isClient &&
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowInternalPaymentsPage:
      !isClient &&
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowKnowledgeBasePage:
      UserUtils.hasPermission(Constants.PERM_MOD_KNOWLEDGE_BASE, permissions) &&
      getDefaultModuleStateFromModuleSettings('knowledgeBase'),
    allowSubscriptionPage:
      !isUnAuth &&
      !isClient &&
      (UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions) ||
        getDefaultModuleStateFromModuleSettings('payments')),
    // settings page access configurations
    allowSettingsCustomizationPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_CUSTOMIZATION,
      permissions,
    ),
    // Settings/payments page is only visible for clients
    // when permission is allowed and the payments module is enabled in portalConfig,
    allowSettingsBillingPage:
      isClient &&
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_BILLING,
        permissions,
      ) &&
      getDefaultModuleStateFromModuleSettings('payments'),
    allowSettingsModuleManagement: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_MODULE_MANAGEMENT,
      permissions,
    ),
    allowSettingsFiles:
      UserUtils.hasPermission(Constants.PERM_MOD_SETTINGS_FILES, permissions) &&
      getDefaultModuleStateFromModuleSettings('files'),
    allowSettingsModulePayments:
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_MODULE_PAYMENTS,
        permissions,
      ) && getDefaultModuleStateFromModuleSettings('payments'),
    allowSettingsMainPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_MAIN_PG,
      permissions,
    ),
    allowSettingsClientAuth:
      !isClient &&
      UserUtils.hasPermission(Constants.PERM_MOD_SETTINGS_MAIN_PG, permissions),
    allowSettingsAssistant:
      !isClient &&
      UserUtils.hasPermission(Constants.PERM_MOD_SETTINGS_MAIN_PG, permissions),
    allowSettingsModuleMessaging:
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_MESSAGING,
        permissions,
      ) && getDefaultModuleStateFromModuleSettings('messages'),
    allowSettingsMembersPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_MEMBERS,
      permissions,
    ),
    allowSettingsAuditLog:
      !isClient &&
      UserUtils.hasPermission(
        Constants.PERM_MOD_SETTINGS_AUDIT_LOG,
        permissions,
      ),
    allowSettingsApiPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_API,
      permissions,
    ),
    allowSettingsAppsPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_APPS,
      permissions,
    ),
    // App Builder workspaces can manage secrets before connectors roll out.
    // Connector content inside the page keeps its independent rollout gate.
    allowSettingsConnectionsPage:
      (EnableStudioConnectors || EnableAppBuilder) &&
      !isClient &&
      UserUtils.hasPermission(Constants.PERM_MOD_SETTINGS_APPS, permissions),
    allowSettingsExtensionsPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_EXTENSIONS,
      permissions,
    ),
    allowSettingsDomainPage: UserUtils.hasOperation(
      Constants.PERM_MOD_SETTINGS_DOMAINS,
      GENERAL_OPERATIONS.UPDATE,
      permissions,
    ),
    allowSettingsClientNotificationsPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_CLIENT_NOTIFICATIONS,
      permissions,
    ),
    allowFormsInternal:
      getDefaultModuleStateFromModuleSettings('forms') &&
      UserUtils.hasPermission(Constants.PERM_MOD_FORMS, permissions) &&
      !isClient,
    allowFormsClient:
      getDefaultModuleStateFromModuleSettings('forms') &&
      UserUtils.hasPermission(Constants.PERM_MOD_FORMS, permissions),
    allowClientDetailsPage: !isClient,
    allowCompaniesDetailsPage: !isClient,
    allowSettingsPlanPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_PLANS,
      permissions,
    ),
    allowSettingsReferralPage: !isClient && !hideReferrals,
    allowReferralPage: !isClient && !hideReferrals,
    allowExtensionsPage: true,
    allowNotificationSettings: !isClient,
    allowClientImportPage: !isClient && canCreateClient,
    allowInboxPage: !isClient && !isInboxInProductNotificationsDisabled,
    allowAppSetupPage: UserUtils.hasPermission(
      Constants.PERM_MOD_SETTINGS_MODULE_MANAGEMENT,
      permissions,
    ),
    allowContractsPage: getDefaultModuleStateFromModuleSettings('contracts'),
    allowAutomationsPage:
      !isClient ||
      (EnableStudioSidebar &&
        UserUtils.hasPermission(Constants.PERM_MOD_AUTOMATIONS, permissions)),
    allowInvoices:
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowInvoiceDetailsPage:
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowPaymentLinks:
      !isClient &&
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowClientPaymentLinks: true,
    allowProductStore:
      !isClient &&
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
    allowClientStore: getDefaultModuleStateFromModuleSettings('payments'),
    allowClientProductStore:
      isClient &&
      getDefaultModuleStateFromModuleSettings('payments') &&
      UserUtils.hasPermission(Constants.PERM_MOD_PAYMENTS, permissions),
  } as const;

  return {
    routeAccess,
    isLoading: isInstallsLoading,
  };
};

export type RouteAccess = ReturnType<typeof useRouteAccess>['routeAccess'];
